import { useState, useEffect, useRef, useCallback } from "react";

const STORAGE_KEY = "whatsapp_floating_position";
const PADDING = 12;

interface DraggableState {
  position: { x: number; y: number };
  isDragging: boolean;
  hasMoved: boolean;
  buttonRef: React.RefObject<HTMLDivElement | null>;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
}

function getDefaultPosition(): { x: number; y: number } {
  const y = typeof window !== "undefined" ? window.innerHeight - 96 : 600;
  return { x: 24, y };
}

export function useDraggablePosition(): DraggableState {
  const [position, setPosition] = useState(getDefaultPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  // Load saved position on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        const maxX = Math.max(0, window.innerWidth - 72);
        const maxY = Math.max(0, window.innerHeight - 72);
        setPosition({ x: Math.max(12, Math.min(pos.x, maxX)), y: Math.max(12, Math.min(pos.y, maxY)) });
      } catch {
        setPosition(getDefaultPosition());
      }
    }
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    setDragOffset({ x: touch.clientX - rect.left - rect.width / 2, y: touch.clientY - rect.top - rect.height / 2 });
    setIsDragging(true);
    setHasMoved(false);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent): void => {
    if (!isDragging || !buttonRef.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const rect = buttonRef.current.getBoundingClientRect();
    let newX = touch.clientX - dragOffset.x - rect.width / 2;
    let newY = touch.clientY - dragOffset.y - rect.height / 2;

    setPosition((prev) => {
      if (Math.abs(newX - prev.x) > 5 || Math.abs(newY - prev.y) > 5) {
        setHasMoved(true);
      }
      const maxX = window.innerWidth - rect.width - PADDING;
      const maxY = window.innerHeight - rect.height - PADDING;
      newX = Math.max(PADDING, Math.min(newX, maxX));
      newY = Math.max(PADDING, Math.min(newY, maxY));
      return { x: newX, y: newY };
    });
  }, [isDragging, dragOffset]);

  const handleTouchEnd = useCallback((): void => {
    if (isDragging) {
      setPosition((pos) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
        return pos;
      });
      setIsDragging(false);
      setTimeout(() => setHasMoved(false), 100);
    }
  }, [isDragging]);

  return { position, isDragging, hasMoved, buttonRef, handleTouchStart, handleTouchMove, handleTouchEnd };
}
